import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  abandonGeneration,
  createDream,
  deleteDream,
  fetchDreams,
  fetchGenerationJobs,
  fetchGenerationProviders,
  publishGeneration,
  refreshGeneration,
  startGeneration,
  unpublishGeneration,
  type Dream,
  type GenerationJob,
  type GenerationProvider,
} from "./dreams";
import {
  ACTIVE_GENERATION_STATUSES,
  isAbandonable,
  MAX_POLL_INTERVAL_MS,
  POLL_INTERVAL_MS,
  statusMessageFor,
} from "./generation-polling";
import { fetchMedia, type MediaItem } from "./media";

interface DreamStudioProps {
  libraryRevision?: number;
}

export function DreamStudio({ libraryRevision = 0 }: DreamStudioProps) {
  const [dreams, setDreams] = useState<Dream[]>([]);
  const [jobs, setJobs] = useState<GenerationJob[]>([]);
  const [providers, setProviders] = useState<GenerationProvider[]>([]);
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [aspectRatio, setAspectRatio] = useState<"16:9" | "9:16">("16:9");
  const [durationSeconds, setDurationSeconds] = useState<4 | 6 | 8>(8);
  const [status, setStatus] = useState("Loading dream drafts…");
  const [busy, setBusy] = useState(false);
  const [autoRefreshFailing, setAutoRefreshFailing] = useState(false);
  // Shared by the poller and the manual "Check status" button so the two can
  // never have a refresh in flight at the same time.
  const refreshInFlight = useRef(false);

  useEffect(() => {
    void Promise.all([
      fetchDreams(),
      fetchMedia(),
      fetchGenerationJobs(),
      fetchGenerationProviders(),
    ])
      .then(([nextDreams, nextMedia, nextJobs, nextProviders]) => {
        setDreams(nextDreams);
        setMedia(nextMedia);
        setJobs(nextJobs);
        setProviders(nextProviders);
        setStatus(nextDreams.length ? "" : "No dream drafts yet.");
      })
      .catch((error: unknown) => {
        setStatus(error instanceof Error ? error.message : "Could not load Dreams");
      });
  }, [libraryRevision]);

  const mediaById = useMemo(
    () => new Map(media.map((item) => [item.id, item])),
    [media],
  );

  const applyJob = useCallback((updated: GenerationJob) => {
    setJobs((current) => current.map((item) => item.id === updated.id ? updated : item));
  }, []);

  // A joined string, not an array: re-polling the same jobs must not restart
  // the effect, or every response would cancel the timer and schedule a new
  // one, and a job that never leaves "generating" would be polled forever at
  // whatever rate responses arrive.
  const activeJobIds = useMemo(
    () => jobs
      .filter((job) => ACTIVE_GENERATION_STATUSES.has(job.status))
      .map((job) => job.id)
      .join(","),
    [jobs],
  );

  useEffect(() => {
    if (!activeJobIds) {
      setAutoRefreshFailing(false);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let consecutiveFailures = 0;

    function schedule() {
      if (cancelled) return;
      // Chained timeouts rather than an interval: the next poll is only armed
      // once the previous one has settled, so requests cannot overlap or pile
      // up behind a slow Vertex response.
      const delay = Math.min(POLL_INTERVAL_MS * 2 ** consecutiveFailures, MAX_POLL_INTERVAL_MS);
      timer = setTimeout(() => void tick(), delay);
    }

    async function tick() {
      if (cancelled) return;
      if (refreshInFlight.current) {
        schedule();
        return;
      }
      refreshInFlight.current = true;
      try {
        for (const jobId of activeJobIds.split(",")) {
          const updated = await refreshGeneration(jobId);
          if (cancelled) return;
          applyJob(updated);
          if (!ACTIVE_GENERATION_STATUSES.has(updated.status)) {
            setStatus(statusMessageFor(updated));
          }
        }
        consecutiveFailures = 0;
        setAutoRefreshFailing(false);
      } catch {
        if (cancelled) return;
        // Back off instead of hammering a rate-limited or failing Vertex.
        consecutiveFailures += 1;
        setAutoRefreshFailing(true);
      } finally {
        refreshInFlight.current = false;
      }
      schedule();
    }

    schedule();
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [activeJobIds, applyJob]);

  function toggleMedia(mediaId: string) {
    setSelectedIds((current) => current.includes(mediaId)
      ? current.filter((id) => id !== mediaId)
      : [...current, mediaId]);
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!title.trim() || !prompt.trim() || selectedIds.length === 0) return;
    setBusy(true);
    setStatus("Saving draft…");
    try {
      const dream = await createDream({
        title: title.trim(),
        prompt: prompt.trim(),
        media_ids: selectedIds,
      });
      setDreams((current) => [dream, ...current]);
      setTitle("");
      setPrompt("");
      setSelectedIds([]);
      setStatus("Draft saved. No generation credits were used.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Could not save draft");
    } finally {
      setBusy(false);
    }
  }

  async function discard(dream: Dream) {
    setBusy(true);
    setStatus("");
    try {
      await deleteDream(dream.id);
      setDreams((current) => current.filter((item) => item.id !== dream.id));
      setJobs((current) => current.filter((job) => job.dream_id !== dream.id));
      setStatus("Draft discarded.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Could not discard draft");
    } finally {
      setBusy(false);
    }
  }

  async function generate(dream: Dream, provider: GenerationProvider) {
    const estimatedCost = provider.price_per_second * durationSeconds;
    if (provider.paid && !window.confirm(
      `Generate ${durationSeconds} seconds with ${provider.label} for an estimated $${estimatedCost.toFixed(2)}?`,
    )) return;
    setBusy(true);
    setStatus(provider.paid ? "Submitting paid generation…" : "Queueing simulation…");
    try {
      const job = await startGeneration(dream.id, {
        provider: provider.id,
        aspect_ratio: aspectRatio,
        duration_seconds: durationSeconds,
        ...(provider.paid ? { confirmed_estimated_cost: estimatedCost } : {}),
      });
      setJobs((current) => [job, ...current]);
      if (job.status === "uncertain") {
        setStatus("Vertex did not confirm acceptance. Check status before doing anything else.");
      } else {
        setStatus(provider.paid
          ? "Generation submitted. Use Check status in a minute or two."
          : "Simulation queued. No provider API or credits were used.");
      }
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Could not queue generation");
    } finally {
      setBusy(false);
    }
  }

  async function refresh(job: GenerationJob) {
    setBusy(true);
    setStatus("Checking generation…");
    refreshInFlight.current = true;
    try {
      const updated = await refreshGeneration(job.id);
      applyJob(updated);
      setStatus(statusMessageFor(updated));
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Could not refresh generation");
    } finally {
      refreshInFlight.current = false;
      setBusy(false);
    }
  }

  async function abandon(job: GenerationJob) {
    if (!window.confirm(
      job.status === "uncertain"
        ? "Abandon this uncertain request? It may still finish and be charged. Check Vertex AI or wait before allowing another paid generation."
        : "Abandon this stuck request? It has not progressed in over an hour, but it may still finish and be charged. Check Vertex AI before allowing another paid generation.",
    )) return;
    setBusy(true);
    setStatus("Abandoning the request…");
    try {
      const updated = await abandonGeneration(job.id);
      applyJob(updated);
      setStatus("Request abandoned. A new generation is now allowed.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Could not abandon generation");
    } finally {
      setBusy(false);
    }
  }

  async function setPublished(job: GenerationJob, publish: boolean) {
    setBusy(true);
    setStatus(publish ? "Publishing Dream…" : "Taking Dream off the channel…");
    try {
      const updated = publish
        ? await publishGeneration(job.id)
        : await unpublishGeneration(job.id);
      setJobs((current) => current.map((item) => item.id === updated.id ? updated : item));
      setStatus(publish ? "Dream published to Cat TV." : "Dream removed from Cat TV.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Could not update publication");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="dream-panel" aria-labelledby="dream-heading">
      <div className="library-heading">
        <div>
          <p className="eyebrow">02 · Generative workshop</p>
          <h2 id="dream-heading">Dreams</h2>
        </div>
        <span>{dreams.length} {dreams.length === 1 ? "draft" : "drafts"}</span>
      </div>

      <form className="dream-form" onSubmit={(event) => void save(event)}>
        <div className="dream-fields">
          <label>
            <span>Dream title</span>
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={120}
              placeholder="The midnight expedition"
              required
            />
          </label>
          <label>
            <span>What should happen?</span>
            <textarea
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              maxLength={2000}
              placeholder="Keep the cat recognizable while the living room becomes a moonlit jungle…"
              required
            />
          </label>
        </div>

        <fieldset disabled={busy || media.length === 0}>
          <legend>Source memories (choose up to 8)</legend>
          {media.length === 0 ? (
            <p>Add photos or videos to the Library first.</p>
          ) : (
            <div className="dream-source-grid">
              {media.map((item) => {
                const selected = selectedIds.includes(item.id);
                const atLimit = selectedIds.length >= 8 && !selected;
                return (
                  <label key={item.id} className={selected ? "selected" : ""}>
                    <input
                      type="checkbox"
                      checked={selected}
                      disabled={busy || atLimit}
                      onChange={() => toggleMedia(item.id)}
                    />
                    <span>{item.title}</span>
                    <small>{item.content_type.startsWith("video/") ? "Movie" : "Photo"}</small>
                  </label>
                );
              })}
            </div>
          )}
        </fieldset>
        <div className="dream-submit-row">
          <small>Draft only. Generation will be connected separately.</small>
          <button type="submit" disabled={busy || selectedIds.length === 0}>
            {busy ? "Working…" : "Save dream draft"}
          </button>
        </div>
      </form>

      {status && <p className="library-status" role="status">{status}</p>}
      {dreams.length > 0 && (
        <>
          <div className="generation-settings" aria-label="Generation settings">
            <label>
              <span>Frame</span>
              <select
                value={aspectRatio}
                disabled={busy}
                onChange={(event) => setAspectRatio(event.target.value as "16:9" | "9:16")}
              >
                <option value="16:9">Landscape 16:9</option>
                <option value="9:16">Portrait 9:16</option>
              </select>
            </label>
            <label>
              <span>Length</span>
              <select
                value={durationSeconds}
                disabled={busy}
                onChange={(event) => setDurationSeconds(Number(event.target.value) as 4 | 6 | 8)}
              >
                <option value={4}>4 seconds</option>
                <option value={6}>6 seconds</option>
                <option value={8}>8 seconds</option>
              </select>
            </label>
            <small>These settings apply to the next generation request.</small>
          </div>
          <div className="dream-list">
            {dreams.map((dream) => {
              const dreamJobs = jobs.filter((job) => job.dream_id === dream.id);
              const activeJob = dreamJobs.find((job) => ACTIVE_GENERATION_STATUSES.has(job.status));
              return (
              <article key={dream.id}>
              <div className="dream-state">{dream.status}</div>
              <div>
                <h3>{dream.title}</h3>
                <p>{dream.prompt}</p>
                <small>
                  {dream.media_ids.map((id) => mediaById.get(id)?.title ?? "Unavailable memory").join(" · ")}
                </small>
                {dreamJobs.length > 0 && (
                  <div className="generation-history">
                    {dreamJobs.map((job) => (
                      <div key={job.id}>
                        <span>{job.status}</span>
                        <small>{job.model} · {job.duration_seconds}s · {job.aspect_ratio}</small>
                        {job.error && <small>{job.error}</small>}
                        {ACTIVE_GENERATION_STATUSES.has(job.status) && (
                          <>
                            <button type="button" disabled={busy} onClick={() => void refresh(job)}>
                              Check status
                            </button>
                            <small className="auto-refresh-note" role="status">
                              {autoRefreshFailing
                                ? "Checking automatically — the last check failed, retrying less often."
                                : "Checking automatically every 10 seconds."}
                            </small>
                          </>
                        )}
                        {isAbandonable(job) && (
                          <button type="button" disabled={busy} onClick={() => void abandon(job)}>
                            {job.status === "uncertain"
                              ? "Abandon uncertain request"
                              : "Abandon stuck request"}
                          </button>
                        )}
                        {job.status === "ready" && job.output_media_id && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void setPublished(job, job.published_at === null)}
                          >
                            {job.published_at ? "Remove from Cat TV" : "Publish to Cat TV"}
                          </button>
                        )}
                        {job.published_at && <small className="published-label">Live on Cat TV</small>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="dream-actions">
                {providers.map((provider) => (
                  <button
                    key={provider.id}
                    type="button"
                    disabled={busy || !provider.available || Boolean(activeJob)}
                    title={provider.description}
                    onClick={() => void generate(dream, provider)}
                  >
                    {provider.available
                      ? `Try ${provider.label}${provider.paid ? ` · ~$${(provider.price_per_second * durationSeconds).toFixed(2)}` : ""}`
                      : `${provider.label} not connected`}
                  </button>
                ))}
                <button className="discard" type="button" disabled={busy || Boolean(activeJob)} onClick={() => void discard(dream)}>Discard</button>
              </div>
              </article>
            );})}
          </div>
        </>
      )}
    </section>
  );
}
